import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { hostLogin } from "../store/thunks/hostActions";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import { loginSchema } from "../schemas";
import { hostLoginReset } from "../store/slices/hostSlices";
import ReCAPTCHA from "react-google-recaptcha";
import { Mail, Lock, Eye, EyeOff, ArrowRight, HelpCircle } from "lucide-react";
import logo from "../icons/BellhoptLogoMedRes.png";
import UNSPLASH from "../components/unsplashImages";
import { sanitizeNextPath } from "../utils/authRedirect";

const PENDING_HOSPITABLE_OAUTH_KEY = "pending_hospitable_oauth";
const HOSPITABLE_OAUTH_MAX_AGE_MS = 10 * 60 * 1000;
const SAVED_LOGIN_KEY = "saved_login_fields";

const LoginScreen = () => {
  const [initialValues] = useState(() => {
    const saved = sessionStorage.getItem(SAVED_LOGIN_KEY);
    if (saved) {
      sessionStorage.removeItem(SAVED_LOGIN_KEY);
      try {
        return JSON.parse(saved);
      } catch {
        /* ignore */
      }
    }
    return { email: "", password: "" };
  });

  const { values, errors, touched, handleBlur, handleChange } = useFormik({
    initialValues: initialValues,
    validationSchema: loginSchema,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const recaptchaRef = useRef(null);
  const nextPath = sanitizeNextPath(searchParams.get("next"));

  const onCaptchaChange = (token) => setCaptchaToken(token || "");

  const onCaptchaExpired = () => {
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  };

  const { loading, hostInfo, error } = useSelector((state) => state.host);

  const handleLogin = (e) => {
    e.preventDefault();
    if (Object.keys(errors).length === 0 && captchaToken) {
      dispatch(hostLogin({ ...values, captchaResponse: captchaToken }));
    }
  };

  useEffect(() => {
    if (hostInfo) {
      const pendingOauthRaw = sessionStorage.getItem(PENDING_HOSPITABLE_OAUTH_KEY);
      if (pendingOauthRaw) {
        try {
          const pendingOauth = JSON.parse(pendingOauthRaw);
          const createdAt = Number(pendingOauth?.createdAt || 0);
          const isFresh =
            createdAt > 0 && Date.now() - createdAt < HOSPITABLE_OAUTH_MAX_AGE_MS;
          if (pendingOauth?.code && pendingOauth?.state && isFresh) {
            const encodedCode = encodeURIComponent(pendingOauth.code);
            const encodedState = encodeURIComponent(pendingOauth.state);
            navigate(
              `/integrations/hospitable/callback?code=${encodedCode}&state=${encodedState}`,
              { replace: true }
            );
            return;
          }
          sessionStorage.removeItem(PENDING_HOSPITABLE_OAUTH_KEY);
        } catch (_) {
          sessionStorage.removeItem(PENDING_HOSPITABLE_OAUTH_KEY);
        }
      }
      if (nextPath) {
        navigate(nextPath, { replace: true });
        return;
      }
      const hasOta = hostInfo?.ota === true;
      navigate(hasOta ? "/concierge" : "/host", { replace: true });
    }

    if (error) {
      recaptchaRef.current?.reset();
      setCaptchaToken("");
      const t = setTimeout(() => {
        dispatch(hostLoginReset());
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [hostInfo, error, navigate, dispatch, nextPath]);

  return (
    <div
      className="relative w-full h-[100dvh] overflow-hidden bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            width: "calc(100% - 32px)",
            maxWidth: 520,
            margin: "auto",
            top: 20,
            left: 0,
            right: 0,
            zIndex: 20,
            borderRadius: "12px",
          }}
        >
          <strong>{error}</strong>
          {error !== "Please try again in 15 mins." && (
            <span className="block mt-1 text-sm">
              Forgot your password?{" "}
              <Link
                to="/login/forgotPassword"
                className="font-semibold underline"
              >
                Reset it here
              </Link>
            </span>
          )}
        </Alert>
      )}

      <div className="grid h-full grid-cols-1 laptop:grid-cols-[1.05fr_1fr]">
        {/* Left — branded anchor (hidden on mobile, visible tabletOnly+) */}
        <div className="relative hidden tabletOnly:block laptop:block overflow-hidden">
          <img
            src={UNSPLASH.hero}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(17,17,17,0.72) 0%, rgba(17,17,17,0.45) 50%, rgba(239,73,73,0.35) 100%)",
            }}
          />
          <div className="relative h-full flex flex-col justify-between p-10 laptop:p-14">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/80">
                Bellhopt for Hosts
              </span>
            </div>

            <div className="max-w-[460px]">
              <h2
                className="text-[36px] laptop:text-[46px] font-extrabold text-white leading-[1.04]"
                style={{ letterSpacing: -0.6 }}
              >
                The concierge layer for
                <br />
                short-term rentals.
              </h2>
              <p className="mt-4 text-[14px] text-white/80 leading-relaxed max-w-[420px]">
                Pre-arrival groceries, branded guest guidebooks, and monetized
                upgrades — all in one quiet layer on top of your existing stack.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-x-6 max-w-[420px]">
                {[
                  { k: "5-star", v: "Guest experience" },
                  { k: "$25–$100", v: "Per upsell" },
                  { k: "100%", v: "Custom margin" },
                ].map((m) => (
                  <div key={m.k}>
                    <div
                      className="text-[18px] laptop:text-[20px] font-extrabold text-white leading-tight"
                      style={{ letterSpacing: -0.3 }}
                    >
                      {m.k}
                    </div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/60 mt-1">
                      {m.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/50">
              <span>Hosts · Property managers · Operators</span>
              <Link
                to="/support"
                className="inline-flex items-center gap-1 hover:text-white transition-colors"
              >
                <HelpCircle size={11} />
                Support
              </Link>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="relative flex items-center justify-center px-5 tabletOnly:px-10 py-6 overflow-y-auto">
          <div className="w-full max-w-[400px]">
            {/* Mobile logo */}
            <div className="tabletOnly:hidden laptop:hidden flex flex-col items-center mb-6">
              <img src={logo} alt="Bellhopt" className="h-14 w-auto mb-3" />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
                Bellhopt for Hosts
              </span>
            </div>

            <div className="mb-7">
              <div className="hidden tabletOnly:flex laptop:flex items-center gap-2 mb-4">
                <img src={logo} alt="Bellhopt" className="h-8 w-auto" />
              </div>
              <h1
                className="text-[26px] laptop:text-[30px] font-extrabold text-[#111111] leading-tight"
                style={{ letterSpacing: -0.5 }}
              >
                Welcome back.
              </h1>
              <p className="mt-1.5 text-[13px] text-[#5F6368]">
                Sign in to your Bellhopt dashboard.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3.5">
              <Field
                name="email"
                type="email"
                label="Email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && errors.email}
                placeholder="you@company.com"
                icon={<Mail size={14} className="text-[#888888]" />}
              />

              <Field
                name="password"
                type={showPassword ? "text" : "password"}
                label="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && errors.password}
                placeholder="••••••••"
                icon={<Lock size={14} className="text-[#888888]" />}
                right={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-[#888888] hover:text-[#111111] transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                }
              />

              <div className="flex items-center justify-between">
                <div className="scale-[0.88] origin-left">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq"
                    onChange={onCaptchaChange}
                    onExpired={onCaptchaExpired}
                  />
                </div>
                <Link
                  to="/login/forgotPassword"
                  className="text-[11px] font-semibold text-[#5F6368] hover:text-[#111111] transition-colors"
                >
                  Forgot?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading || !captchaToken}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-[12px] bg-[#111111] text-white text-[14px] font-bold hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <CircularProgress color="inherit" size={16} />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-[#EAEAEA] text-center">
              <p className="text-[12px] text-[#5F6368]">
                New to Bellhopt?{" "}
                <Link
                  to="/signup"
                  className="font-bold text-[#111111] hover:text-[#EF4949] transition-colors"
                >
                  Create an account
                </Link>
              </p>
            </div>

            <div className="mt-5 flex items-center justify-center gap-3 text-[10px] text-[#888888]">
              <Link
                to="/privacy"
                className="hover:text-[#111111] transition-colors"
              >
                Privacy
              </Link>
              <span>·</span>
              <Link
                to="/terms"
                className="hover:text-[#111111] transition-colors"
              >
                Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Field({ name, type, label, value, onChange, onBlur, error, placeholder, icon, right }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-1.5"
      >
        {label}
      </label>
      <div
        className={`relative flex items-center rounded-[12px] border transition-colors bg-white ${
          error ? "border-[#EF4444]" : "border-[#EAEAEA] focus-within:border-[#111111]"
        }`}
      >
        <span className="pl-3.5 pr-1 flex-shrink-0">{icon}</span>
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className="flex-1 bg-transparent py-3 pr-2 text-[14px] text-[#111111] placeholder:text-[#888888] outline-none"
        />
        {right && <span className="pr-3 flex-shrink-0">{right}</span>}
      </div>
      {error && (
        <p className="mt-1 text-[11px] font-semibold text-[#EF4444]">{error}</p>
      )}
    </div>
  );
}

export default LoginScreen;
